import React, { ReactNode, useEffect } from "react";
import { useSelector } from "react-redux";
import { isNil, isEmpty } from "ramda";
import {
  CssBaseline,
  Drawer as MuiDrawer,
  Toolbar,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import Nav from "../common/nav/Nav";
import { ApplicationState } from "../store";
import { Data as SessionData } from "../store/session/types";
import ProfileMenu from "../common/profileMenu/ProfileMenu";
import Logo from "../common/helper/Logo";
import useUserAccess from "./../effects/useUserAccess";
// import moment from "moment";
// import { useAuth0 } from "@auth0/auth0-react";

const drawerWidth = 230;

interface Props {
  children: ReactNode;
  name?: string;
  sessionData?: SessionData | null;
}

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  backgroundColor: "var(--dmg-primary) !important",
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: "border-box",
    ...(!open && {
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up("sm")]: {
        width: theme.spacing(9),
      },
    }),
  },
}));

const displayName = (session: SessionData): string => {
  let result = "";
  const preferredName = session.attributes.PreferredName;
  const firstName = session.attributes.FirstName;
  const lastName = session.attributes.LastName;

  if (!isNil(preferredName) && !isEmpty(preferredName)) {
    result = preferredName;
  } else {
    result = `${firstName} ${lastName || ""}`;
  }

  return result;
};

const Layout: React.FC<Props> = (props) => {
  const { sessionData } = props;
  const [open, setOpen] = React.useState(true);
  const session = useSelector((state: ApplicationState) => state.session.data);
  const accessMap = useUserAccess();
  // let lastActivity = moment().toString();
  // const { logout } = useAuth0();

  // const checkUserActive = () => {
  //   const timeInterval = setInterval(() => {
  //     const diffMs = moment().diff(moment(lastActivity), "minutes");
  //     if (diffMs >= inactiveTimeToLogout) {
  //       clearInterval(timeInterval);
  //       logout();
  //     }
  //   }, 10000);
  // };

  // useEffect(() => {
  //   document.addEventListener("mousemove", () => {
  //     lastActivity = moment().toString();
  //   });
  //   document.addEventListener("click", () => {
  //     lastActivity = moment().toString();
  //   });
  //   checkUserActive();
  // }, []);

  const handleDrawerOpen = (): void => {
    setOpen(true);
  };

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const userName = !isNil(sessionData) ? displayName(sessionData) : "";

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar
          sx={{
            pr: "24px",
          }}
        >
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            sx={{
              marginRight: "36px",
              ...(open && { display: "none" }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            sx={{ flexGrow: 1 }}
          >
          Terminal Management System
          </Typography>
          {userName}
          <ProfileMenu
            name={userName}
            id={sessionData?.id}
            email={sessionData?.attributes.Email}
          />
        </Toolbar>
      </AppBar>
      <Drawer sx={{ maxHeight: "100vh" }} variant="permanent" open={open}>
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            px: [1],
          }}
        >
          <IconButton onClick={toggleDrawer}>
            <ChevronLeftIcon />
          </IconButton>
        </Toolbar>
        {open &&
          (process.env.IDP === "NAB" ? (
            <div style={{ padding: "10px 0px 60px 0px", alignSelf: "center" }}>
              <img
                src="/images/nabdmg.png"
                alt="Nab/Dmg Logo"
                style={{ width: "180px" }}
              />
            </div>
          ) : (
            <Logo />
          ))}
        <Nav session={session} access={accessMap} />
      </Drawer>
      <Box
        id="main-content"
        component="main"
        sx={{
          flexGrow: 1,
          height: "100vh",
          overflow: "auto",
        }}
      >
        {props.children}
      </Box>
    </Box>
  );
};

export default Layout;
